// core
import {Component, ElementRef, HostListener, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {distinctUntilChanged, take, tap} from 'rxjs/operators';

// constants
import {AppConstants} from '../../utils/constants';
import {ActivatedRoute, Router} from "@angular/router";

// services
import {TranslateService} from '@ngx-translate/core';
import {CustomerService} from '../services/customer-service.service';
import {ImageTypeValidation} from '../../shared/services/image-type-validation';
import {ImageSizeValidation} from '../../shared/services/image-size-validation';
import {UserRegistrationServiceService} from '../../user-registration/service/user-registration-service.service';
import {RolesService} from '../../role/roles.service';
import {ErrorMessageService} from '../../core/alert-message/alert-message.service';

// utils
import {UtilsService} from '../../utils/utils.service';
import {RolesUtils} from '../../utils/roles.utils';

// models
import {CustomerModel} from '../models/customer-model';
import {UnsavedChangesComponent} from "../../shared/unsaved-changes-component";

//import { ValidateRoles } from '../../role/role-async.validator';

@Component({
  selector: 'create-edit-customer',
  templateUrl: './create-edit-customer.component.html',
  styleUrls: ['./create-edit-customer.component.scss'],
  standalone: false
})
export class CreateEditCustomerComponent implements UnsavedChangesComponent {

  @ViewChild('fileInput', {static: true}) myFileInput: ElementRef;

  private customerDetailsForm: UntypedFormGroup;
  private newCustomer: CustomerModel = new CustomerModel();
  private editedCustomer: CustomerModel = new CustomerModel();

  // used for custom styling of the select component
  private selectWithInitialValue = false;
  private imageWidth: number = 100;
  private imageHeight: number = 100;
  private homePath = AppConstants.homePage;

  private currentMode: string;
  private removeLogo: boolean;

  private listOfTargetGroups: Array<any> = new Array<any>();
  private listOfCountries: Array<any> = new Array<any>();
  private allPossibleRolesWithId: Array<any>;
  private allPossibleRoles: Array<any> = new Array<any>();

  private appManagerRolesValidationResult: boolean = true;

  private loading = false;

  private file: File;

  private isEdit: boolean = false;

  private filesModified: boolean = false;

  private userSavedTheChanges: boolean = false;

  constructor(private _utilsService: UtilsService,
              private _formBuilder: UntypedFormBuilder,
              private _translateService: TranslateService,
              private _customerService: CustomerService,
              private _router: Router,
              private _activatedRoute: ActivatedRoute,
              private _userRegistrationService: UserRegistrationServiceService,
              private _rolesService: RolesService,
              private _rolesUtils: RolesUtils,
              private _errorMessageService: ErrorMessageService) {
    this.customerDetailsForm = this._formBuilder.group({
      active: [
        {
          value: true,
          disabled: false
        }
      ],
      confirm: [
        {
          value: true,
          disabled: false
        }
      ],
      targetGroup: [
        {
          value: '',
          disabled: false
        },
        Validators.required
      ],
      countryCode: [
        {
          value: '',
          disabled: false
        },
        Validators.required
      ],
      customerNr: [
        {
          value: '',
          disabled: false
        },
        Validators.compose([
          Validators.required,
          Validators.pattern(this._utilsService.getOnlyNumbersRegex()),
          Validators.maxLength(10)
        ])
      ],
      company: [
        {
          value: '',
          disabled: false
        },
        Validators.required
      ],
      companyLogo: [
        {
          value: '',
          disabled: false
        }
      ],
      contactPerson: [
        {
          value: '',
          disabled: false
        }
      ],
      roles: [
        {
          value: [],
          disabled: false
        }
      ]
    }, {
      validator: Validators.compose([ImageTypeValidation.ValidateType('companyLogo'), ImageSizeValidation.ValidateSize('companyLogo')])
    });
  }


  ngOnInit() {
    this._subscribeToRoleChange();
    this.currentMode = this._activatedRoute.snapshot.queryParams['type'];

    if (this.customerDetailsForm.controls.language) {
      this.selectWithInitialValue = true;
    }

    this._userRegistrationService.getTargetGroups().subscribe((response) => {
      this.listOfTargetGroups = this._utilsService.initializeSelectOrMultiselect(response);
    });

    this._userRegistrationService.getCountries().subscribe((response) => {
      this.listOfCountries = this._utilsService.initializeSelectOrMultiselect(response);
    });


    this._rolesService.getAllRoles().subscribe((response) => {
      this.allPossibleRolesWithId = response;
    });

    // Keep in mind that "confirmed roles" are not shown. They can be set by changing the targetgroup or combobox "Confirm".
    // They will be saved but they are not visible in the frontend.

    const customerNumber = this._activatedRoute.snapshot.params.customerNumber;
    if (customerNumber && this.isEditMode()) {
      this.isEdit = true;
      this._customerService.getCustomerByCustomerNumber(customerNumber).subscribe(res => {
        this.editedCustomer = res;
        this._initFormWithExistingCustomerData();
        this._disableFields();
      })
    }
    else {
      this.customerDetailsForm.controls.confirm.disable();
    }
  }

  /**
   * Subscribe the valueChangeEvent for roles in order to run a validation
   **/
  private _subscribeToRoleChange() {
    this.customerDetailsForm.controls.roles.valueChanges.pipe(
      distinctUntilChanged(),
      tap(term => {
        if (this.customerDetailsForm.controls.roles.valid) {
          this._onRoleChange(term);
        }
      })
    );

  }

  /**
   * Validates roles when changes are made
   **/
  private _onRoleChange(term: any) {
    if (term) {
      this._rolesService.validateAppManagerRoles(term).pipe(take(1)).subscribe({
        next: (data) => {
          if (data == "true") {
            this.appManagerRolesValidationResult = true;
          } else {
            this.appManagerRolesValidationResult = false;
          }
        },
        error: error => {
          this.appManagerRolesValidationResult = false;
        }
      });
    }
  }

  public getCustomerDetailsForm() {
    return this.customerDetailsForm;
  }

  public getEditedCustomer() {
    return this.editedCustomer;
  }

  public getSelectWithInitialValue() {
    return this.selectWithInitialValue;
  }

  public getImageWidth() {
    return this.imageWidth;
  }

  public getImageHeight() {
    return this.imageHeight;
  }

  public getHomePath() {
    return this.homePath;
  }

  public isEditMode(): boolean {
    return this.currentMode === AppConstants.EDIT_MODE;
  }

  public isCreateMode(): boolean {
    return this.currentMode === AppConstants.CREATE_MODE;
  }

  public getListOfTargetGroups() {
    return this.listOfTargetGroups;
  }

  public getListOfCountries() {
    return this.listOfCountries;
  }

  public getAllPossibleRoles() {
    return this.allPossibleRoles;
  }

  public getLoading() {
    return this.loading;
  }

  public getAppManagerRolesValidationResult() {
    return this.appManagerRolesValidationResult;
  }


  public isDirty() {
    return this.isEdit && (this.customerDetailsForm.dirty || this.filesModified) && !this.userSavedTheChanges;
  }

  @HostListener('window:beforeunload')
  canUnloadWindow() {
    return !this.isEdit || (!this.customerDetailsForm.dirty && !this.filesModified);
  }

  public onTargetGroupDeselected(option) {
    this.selectWithInitialValue = false;
    this.customerDetailsForm.controls.roles.reset();
  }

  public onTargetGroupSelected(option) {
    this.selectWithInitialValue = true;
    this.customerDetailsForm.controls.roles.reset();

    let selectExistingCustomerRolesForTargetGroup: boolean = (
      (this.isEditMode())
      && (this.editedCustomer.customerCategory == option || (option && this.editedCustomer.customerCategory == option.value))
    );

    if (option) {
      // selected currently assigned TargetGroup
      if (selectExistingCustomerRolesForTargetGroup) {
        this.setRolesForCustomer(this.editedCustomer.customerNumber)
        this.customerDetailsForm.controls.confirm.setValue(this.editedCustomer.confirmed);

      }
      // changed TargetGroup
      else if (this.editedCustomer.customerCategory != null && this.editedCustomer.customerNumber) {
        let newTargetGroup = option.value ? option.value : option;
        this.setRolesForCustomerWithUpdatedTargetGroup(this.editedCustomer.customerNumber, newTargetGroup)
        this.customerDetailsForm.controls.confirm.setValue(true);

      }
      // TargetGroup selected for new customer
      else {
        let newTargetGroup = option.value ? option.value : option;
        this.setRolesForNewCustomer(newTargetGroup)
      }
    }
  }

  private setRolesForCustomer(customerNumber) {
    this._rolesService.getAssignedCustomerRoles(customerNumber)
      .subscribe((rolesOfCustomer) => {
        this.customerDetailsForm.controls.roles.setValue(this._constructExistingRolesList(rolesOfCustomer));
      });
    this._rolesService.getAssignableCustomerRoles(customerNumber)
      .subscribe((selectableRoles) => {
        this.allPossibleRoles = this._initializeAuthorizations(selectableRoles);
      });
  }

  private setRolesForCustomerWithUpdatedTargetGroup(customerNumber, newTargetGroup) {
    this._rolesService.getAssignedCustomerRolesForNewTargetGroup(customerNumber, newTargetGroup)
      .subscribe((rolesOfCustomer) => {
        this.customerDetailsForm.controls.roles.setValue(this._constructExistingRolesList(rolesOfCustomer));
      });

    this._rolesService.getAssignableCustomerRolesForNewTargetGroup(customerNumber, newTargetGroup)
      .subscribe((selectableRoles) => {
        this.allPossibleRoles = this._initializeAuthorizations(selectableRoles);
      });
  }

  private setRolesForNewCustomer(targetGroup) {
    this._rolesService.getDefaultRolesForTargetGroup(targetGroup)
      .subscribe((rolesOfCustomer) => {
        this.customerDetailsForm.controls.roles.setValue(this._constructExistingRolesList(rolesOfCustomer));
      });
    this._rolesService.getAssignableRolesForTargetGroup(targetGroup)
      .subscribe((selectableRoles) => {
        this.allPossibleRoles = this._initializeAuthorizations(selectableRoles);
      });
  }

  private _constructExistingRolesList(roles: Array<any>) {
    const list: Array<any> = new Array<any>();

    roles.forEach(role => {
      if (role.roleType === "User") {
        list.push(role.roleKey || role.key);
      }
    });

    return list;
  }

  public saveCustomer(formValues: any) {
    if (this.currentMode === 'create') {
      this._createCustomer(formValues);
    } else {
      this._updateCustomer(formValues);
    }
  }

  public navigateBack() {
    this._router.navigateByUrl(AppConstants.slxAdminManageCustomers);
  }

  public onFileInputChange(event) {
    this.filesModified = true;
    if (event.target.files && event.target.files[0]) {
      var image: any = new Image();
      this.file = event.target.files[0];
      var reader: FileReader = new FileReader();

      reader.onloadend = (loadEvent: any) => {
        image.src = loadEvent.target.result;
        this.customerDetailsForm.controls.companyLogo.setValue(image.src);
      };

      reader.readAsDataURL(this.file);

      this.myFileInput.nativeElement.value = '';
    }
  }

  public deleteLogo() {
    this.filesModified = true;
    this.removeLogo = true;
    this.customerDetailsForm.controls.companyLogo.reset();
  }

  public manageUsers() {
    this._router.navigate([AppConstants.slxAdminManageUsers], {queryParams: {customerNumber: this.editedCustomer.customerNumber}});
  }

  private _initFormWithExistingCustomerData() {
    this.customerDetailsForm.controls.active.setValue(this.editedCustomer.active);
    this.customerDetailsForm.controls.confirm.setValue(this.editedCustomer.confirmed);
    this.customerDetailsForm.controls.targetGroup.setValue(this.editedCustomer.customerCategory);

    this.onTargetGroupSelected(this.editedCustomer.customerCategory);

    this.customerDetailsForm.controls.customerNr.setValue(this.editedCustomer.customerNumber);
    if (this.editedCustomer.countryCode != null) {
      this.customerDetailsForm.controls.countryCode.setValue("country." + this.editedCustomer.countryCode.toLocaleLowerCase());
    }
    this.customerDetailsForm.controls.company.setValue(this.editedCustomer.customerName);

    this.customerDetailsForm.controls.companyLogo.setValue(this.editedCustomer.logo.fileAsBase64);

    if (this.editedCustomer.contactPersonFirstName != null || this.editedCustomer.contactPersonLastName != null) {
      this.customerDetailsForm.controls.contactPerson.setValue(this._utilsService.concatFirstNameWithLastName(this.editedCustomer.contactPersonFirstName, this.editedCustomer.contactPersonLastName));
    }

    if (this.editedCustomer.confirmed) {
      this.customerDetailsForm.controls.confirm.disable();
    }
  }

  private _disableFields() {
    this.customerDetailsForm.controls.customerNr.disable({onlySelf: true});
    this.customerDetailsForm.controls.contactPerson.disable({onlySelf: true});
  }

  private _initializeAuthorizations(data) {
    let list: Array<any> = [];
    let i = 0;

    if (data) {
      data.map((element) => {
        let prefix = this._rolesUtils.getPrefix(element);
        return this._translateService.get(element.roleKey || element.key)
          .subscribe((translatedElement) => {
            list[i] = {
              value: element.roleKey || element.key,
              label: prefix + " | " + translatedElement,
              prefix: prefix,
              translatedRole: translatedElement
            };
            i += 1;
          })
      });
    }
    list.sort((a, b) => b.prefix.localeCompare(a.prefix) || a.translatedRole.localeCompare(b.translatedRole));
    return list;
  }

  private _createCustomer(formValues: any) {
    this.loading = true;

    const newRoles = this._getRoleIds(formValues.roles);
    this.newCustomer = CreateEditCustomerComponent._getCustomerModel(formValues, newRoles);

    if (this.customerDetailsForm.controls.companyLogo.value) {
      const imageUploadData = this._utilsService.getImageUploadData(this.file, this.customerDetailsForm.controls.companyLogo.value);
      this._customerService.saveCustomerChanges(this.newCustomer, imageUploadData).pipe(take(1)).subscribe({
        next: (res) => {
          this.loading = false;
          this.navigateBack();
        },
        error: (error) => {
          this.loading = false;
        }
      });
    } else {
      this._customerService.saveCustomerChanges(this.newCustomer).pipe(take(1)).subscribe({
        next: (res) => {
          this.loading = false;
          this.navigateBack();
        },
        error: (error) => {
          this.loading = false;
        }
      });
    }
  }

  public cancel() {
    this.navigateBack();
  }

  private _updateCustomer(formValues: any) {
    this.loading = true;

    let newRoles = this._getRoleIds(formValues.roles);
    const updatedCustomer = CreateEditCustomerComponent._getCustomerModel(formValues, newRoles, 'edit');
    updatedCustomer.id = this.editedCustomer.id;
    updatedCustomer.customerNumber = this.editedCustomer.customerNumber;
    // When removing image without uploading a new one
    if (this.removeLogo && !this.customerDetailsForm.controls.companyLogo.value) {
      this._customerService.saveCustomerChanges(updatedCustomer, null, this.editedCustomer.logo.id, this.removeLogo).pipe(take(1)).subscribe({
        next: (res) => {
          this.afterEdit();
        },
        error: (error) => {
          this.loading = false;
          // OpenErrorModal
          this._errorMessageService.openErrorModal(error);
        }
      });
      // When uploading the same image or image exist but didn't changed
    } else if (this.editedCustomer.logo.fileAsBase64 != null) {
      if (this.editedCustomer.logo.fileAsBase64 === this.customerDetailsForm.controls.companyLogo.value) {
        this._customerService.saveCustomerChanges(updatedCustomer).pipe(take(1)).subscribe({
          next: (res) => {
            this.afterEdit();
          },
          error: (error) => {
            this.loading = false;
            // OpenErrorModal
            this._errorMessageService.openErrorModal(error);
          }
        });
        // When image was changed
      } else {
        const imageUploadData = this._utilsService.getImageUploadData(this.file, this.customerDetailsForm.controls.companyLogo.value);
        if (this.editedCustomer.logo.fileAsBase64 != null && this.customerDetailsForm.controls.companyLogo.value != null) {
          if (this.editedCustomer.logo.fileAsBase64 != this.customerDetailsForm.controls.companyLogo.value) {
            this._customerService.saveCustomerChanges(updatedCustomer, imageUploadData, this.editedCustomer.logo.id).pipe(take(1)).subscribe({
              next: (res) => {
                this.afterEdit();
              },
              error: (error) => {
                this.loading = false;
                // OpenErrorModal
                this._errorMessageService.openErrorModal(error);
              }
            });
          }
        }
      }
      // When uploading a new image
    } else if (this.file != null) {
      const imageUploadData = this._utilsService.getImageUploadData(this.file, this.customerDetailsForm.controls.companyLogo.value);
      this._customerService.saveCustomerChanges(updatedCustomer, imageUploadData, this.editedCustomer.logo.id).pipe(take(1)).subscribe({
        next: (res) => {
          this.afterEdit();
        },
        error: (error) => {
          this.loading = false;
          // OpenErrorModal
          this._errorMessageService.openErrorModal(error);
        }
      });
      // When no image was set (Default)
    } else {
      this._customerService.saveCustomerChanges(updatedCustomer).pipe(take(1)).subscribe({
        next: (res) => {
          this.afterEdit();
        },
        error: (error) => {
          this.loading = false;
          // OpenErrorModal
          this._errorMessageService.openErrorModal(error);
        }
      });
    }
  }

  private afterEdit() {
    this.loading = false;
    this.userSavedTheChanges = true;
    this.navigateBack();
  }

  private _getRoleIds(data: Array<any>) {
    let roleFound;
    let roleIds: Array<any> = new Array<any>();

    if (data) {
      data.map((element) => {
        roleFound = this.allPossibleRolesWithId.find(role => (role.roleKey || role.key) === element);
        if (roleFound != null) {
          roleIds.push({roleId: roleFound.id || roleFound.roleId})
        }
      })
    }

    return roleIds;
  }

  private static _getCustomerModel(formValues: any, newRoles: Array<any>, editCustomer?: string): CustomerModel {
    const customer: CustomerModel = new CustomerModel();
    customer.customerName = formValues.company;
    customer.customerNumber = formValues.customerNr;
    customer.customerCategory = formValues.targetGroup;
    customer.roles = newRoles;
    customer.active = formValues.active;
    customer.countryCode = formValues.countryCode.substring(8).toLocaleUpperCase();

    if (editCustomer) {
      customer.confirmed = formValues.confirm;
    } else {
      customer.confirmed = true;
    }

    return customer;
  }

}

